<template>
    <div class="questionnaire-auth">
        <AuthForm
            ref="authForm"
            :registration-errors="errors"
            :registration-disabled="fetchingNewCustomerSubmit"
            hide-back-button
            @login="handleLoginSuccess"
            @register="submitNewCustomer"
            @page-email="emit('page-email', $event)"
        >
            <template #registration-title>
                {{ signUpStep.title }}
            </template>

            <template
                v-if="signUpStep.description"
                #registration-description
            >
                {{ signUpStep.description }}
            </template>

            <template
                v-if="signUpStep.submitButtonLabel"
                #registration-submit
            >
                {{ signUpStep.submitButtonLabel }}
            </template>
        </AuthForm>
    </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'
import { storeToRefs } from 'pinia'

import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import type {
    CustomerRegistrationResponseFragment,
    QuestionnaireSignUpStepFragment,
} from '@/graphql/api'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'

import AuthForm, { type Redirect } from '@/components/user/auth/AuthForm.vue'

defineProps<{
    fetchingNewCustomerSubmit: boolean
    errors: ErrorCollectionWithTraces
    signUpStep: QuestionnaireSignUpStepFragment
}>()

const emit = defineEmits<{
    'login-success': []
    'submit-new-customer': [successCallback: () => void]
    'page-email': [boolean]
}>()

const authFormRef = useTemplateRef('authForm')

defineExpose({
    handleRegistrationResponse: (
        response: CustomerRegistrationResponseFragment | undefined,
        redirect: Redirect | null
    ) => {
        authFormRef.value?.handleRegistrationResponse(response, redirect)
    },
    goToPageSoo: () => authFormRef.value?.goToPageSso(),
})

const { customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const handleLoginSuccess = () => {
    emit('login-success')
}

const submitNewCustomer = async (successCallback: () => void) => {
    const customer = customerRegistrationInput.value

    if (!customer) {
        return
    }

    emit('submit-new-customer', successCallback)
}
</script>

<style lang="scss" scoped>
.questionnaire-auth {
    position: relative;
    width: 100%;
    min-height: 92vh;
    max-width: 380px;

    .registration-form,
    .login-form {
        :deep(.signin-switch) {
            bottom: 100px;
        }
    }
}
</style>
